import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { Metric } from '../../interfaces/metric.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { MultiMetricByCategoryListComponent } from '../multi-metric-by-category-list/multi-metric-by-category-list.component';
import { MetricSelectorModalDirective } from '../metric-selector-modal/metric-selector-modal.directive';

/**
 * Multi Select Metric Modal
 *
 * A modal that allows users to select at most one metric.
 * This modal is opened by metric-autocomplete-in-place 'handleButtonClick'
 *
 * Inputs:
 *   maxMetrics: The maximum number of metrics that can be selected. Default null (No limit)
 *
 * Outputs:
 *   selectMetricsHandler: An event emitter that emits the selected metric.
 */
@Component({
    standalone: false,
    selector: 'ycn-multi-metric-selector-modal',
    templateUrl: './multi-metric-selector-modal.template.html',
})
export class MultiMetricSelectorModalComponent extends MetricSelectorModalDirective {
    @Input() maxMetrics: number | null = null;

    @Output() submitModalHandler: EventEmitter<Metric[]> = new EventEmitter<Metric[]>();

    @ViewChild(MultiMetricByCategoryListComponent)
    multiMetricByCategoryListComponent!: MultiMetricByCategoryListComponent;

    errorText: string = '';

    submitModal(): void {
        const metrics: Metric[] = this.multiMetricByCategoryListComponent.getSelectedMetrics();
        this.submitModalHandler.emit(this.featureService.validateFeatureNeeded(metrics, this.featureNeededModal));
        this.closeModal();
    }

    selectedItemsCount(): number {
        if (this.multiMetricByCategoryListComponent) {
            const metrics: Metric[] = this.multiMetricByCategoryListComponent.getSelectedMetrics();
            return metrics.length;
        }
        return 0;
    }

    isSubmissionEnabled(): boolean {
        if (this.multiMetricByCategoryListComponent) {
            const metrics: Metric[] = this.multiMetricByCategoryListComponent.getSelectedMetrics();
            return metrics.length > 0;
        }
        return false;
    }

    resetModal(): void {
        this.multiMetricByCategoryListComponent.reset();
        this.handleMaxLimitError(false);
    }

    submitModalAutocomplete(autocompleteResult: AutocompleteResult): void {
        const validResults = this.featureService.validateFeatureNeeded([autocompleteResult], this.featureNeededModal);
        if (validResults.length > 0) {
            const metricMenuItemInfo = this.multiMetricByCategoryListComponent.getMetricInfo(
                autocompleteResult.search_id
            );
            this.multiMetricByCategoryListComponent.selectMetric(
                {
                    features_needed: autocompleteResult.features_needed,
                    internal_name: autocompleteResult.search_id,
                    label: autocompleteResult.name,
                } as Metric,
                metricMenuItemInfo.categoryLabel,
                metricMenuItemInfo.sectionLabel,
                metricMenuItemInfo.menuLabel
            );
            this.multiMetricByCategoryListComponent.scrollToMetric(metricMenuItemInfo);
        }
    }

    handleMaxLimitError(limitReached: boolean): void {
        this.errorText = limitReached ? 'The maximum metrics limit has been reached' : '';
    }
}
